/**
 * CAPA — Corrective and Preventive Action, with 5 Why root cause analysis.
 *
 * Raised when a client reports an external error. Records what went wrong,
 * the five-why chain, who was involved and why, and the actions taken.
 *
 * People can only be picked from those who actually completed work on the
 * selected job, and each carries the points they earned on their task — the
 * penalty is a multiple of that, shown before the CAPA is saved so nobody is
 * penalised by a number they could not see.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardList, Plus, Search, FileDown, X, Trash2, AlertTriangle, ChevronRight,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const CAPA_ROLES = ['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead',
  'qc_lead', 'production_lead', 'epson_lead'];

const STATUS_STYLE = {
  draft:     'bg-gray-100 text-gray-600 border-gray-300',
  open:      'bg-amber-100 text-amber-800 border-amber-300',
  closed:    'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-gray-100 text-gray-400 border-gray-200 line-through',
};

const EMPTY = {
  project_id: '', job_code: '', reported_by_client: '',
  problem_description: '', immediate_action: '',
  whys: ['', '', '', '', ''], root_cause: '',
  corrective_action: '', preventive_action: '',
  responsible_user_id: '', responsible_user_name: '', target_date: '',
  people: [], penalty_multiplier: 3, status: 'draft',
};

export default function Capa() {
  const { token, user } = useAuth();
  const canManage = CAPA_ROLES.includes(user?.role);
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null);   // null | 'new' | capa id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Job picker + the people who worked on the chosen job
  const [jobQuery, setJobQuery] = useState('');
  const [jobResults, setJobResults] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/capa', {
        ...authHeader,
        params: {
          page,
          page_size: 20,
          status: statusFilter || undefined,
          job_code: jobFilter || undefined,
        },
      });
      setItems(res.data?.items || []);
      setPagination(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load CAPAs');
    } finally {
      setLoading(false);
    }
  }, [authHeader, statusFilter, jobFilter]);

  useEffect(() => { load(1); }, [load]);

  // ── job search ──
  const searchJobs = async (q) => {
    setJobQuery(q);
    if (q.trim().length < 2) { setJobResults([]); return; }
    try {
      // Dedicated search: the main project list covers live jobs only, and a
      // CAPA is usually raised on work already delivered and archived.
      const res = await api.get('/capa/job-search', {
        ...authHeader, params: { q: q.trim() },
      });
      setJobResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setJobResults([]);
    }
  };

  const pickJob = async (p) => {
    const pid = p.id || p._id;
    setForm((f) => ({
      ...f,
      project_id: pid,
      job_code: p.job_code || p.job_number || '',
      project_title: p.title || '',
      client: p.client || '',
      people: [],
    }));
    setJobResults([]);
    setJobQuery(`${p.job_code || ''} ${p.title || ''}`.trim());
    setLoadingPeople(true);
    try {
      const res = await api.get('/capa/project-people', {
        ...authHeader, params: { project_id: pid },
      });
      setCandidates(res.data || []);
    } catch (e) {
      setCandidates([]);
      setError(e.response?.data?.error || 'Could not load the people on this job');
    } finally {
      setLoadingPeople(false);
    }
  };

  // ── people selection ──
  const isPicked = (userId, taskId) =>
    form.people.some((p) => p.user_id === userId && p.task_id === taskId);

  const togglePerson = (cand, task) => {
    setForm((f) => {
      const exists = f.people.some(
        (p) => p.user_id === cand.user_id && p.task_id === task.task_id);
      if (exists) {
        return {
          ...f,
          people: f.people.filter(
            (p) => !(p.user_id === cand.user_id && p.task_id === task.task_id)),
        };
      }
      return {
        ...f,
        people: [...f.people, {
          user_id: cand.user_id,
          user_name: cand.user_name,
          role: cand.role,
          task_id: task.task_id,
          task_name: task.task_name,
          base_points: task.earned_points,
          reason: '',
        }],
      };
    });
  };

  const setReason = (idx, value) => setForm((f) => ({
    ...f,
    people: f.people.map((p, i) => (i === idx ? { ...p, reason: value } : p)),
  }));

  const totalPenalty = useMemo(
    () => form.people.reduce(
      (s, p) => s + (Number(p.base_points) || 0) * (Number(form.penalty_multiplier) || 0), 0),
    [form.people, form.penalty_multiplier]);

  // ── save ──
  const openNew = () => { setForm(EMPTY); setCandidates([]); setJobQuery(''); setEditing('new'); };

  const openExisting = async (id) => {
    try {
      const res = await api.get(`/capa/${id}`, authHeader);
      const c = res.data;
      setForm({ ...EMPTY, ...c, whys: c.whys || ['', '', '', '', ''] });
      setJobQuery(`${c.job_code || ''} ${c.project_title || ''}`.trim());
      setEditing(id);
      if (c.project_id) {
        const pr = await api.get('/capa/project-people', {
          ...authHeader, params: { project_id: c.project_id },
        });
        setCandidates(pr.data || []);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not open that CAPA');
    }
  };

  const save = async (status) => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, status };
      if (editing === 'new') {
        await api.post('/capa', payload, authHeader);
      } else {
        await api.put(`/capa/${editing}`, payload, authHeader);
      }
      setEditing(null);
      await load(pagination.page);
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this CAPA permanently? Cancelling it is usually better.')) return;
    try {
      await api.delete(`/capa/${id}`, authHeader);
      await load(pagination.page);
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed');
    }
  };

  /**
   * Fetch the print-ready report and open it from a blob rather than pointing
   * a new tab at the URL: a plain window.open sends no Authorization header,
   * so the endpoint would reject it. The opened page prints itself, and the
   * browser's print dialog is what saves the PDF.
   */
  const exportPdf = async (id) => {
    try {
      const res = await api.get(`/capa/${id}/export`, { ...authHeader, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
      const w = window.open(url, '_blank');
      if (!w) {
        setError('Allow pop-ups for this site to export the CAPA.');
        return;
      }
      // Revoked late so the new tab has finished loading from it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // responseType 'blob' means the error body is a Blob, not JSON, so the
      // server's message cannot be read out of e.response.data here.
      setError(e.response?.status === 403
        ? 'You do not have permission to export CAPAs.'
        : 'Could not export that CAPA');
    }
  };

  // ── render ──
  if (editing) {
    return (
      <div className="p-6 max-w-5xl">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            {editing === 'new' ? 'New CAPA' : `Edit ${form.capa_number || 'CAPA'}`}
          </h1>
          <button onClick={() => setEditing(null)}
            className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-5">
          {/* Job */}
          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Job</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={jobQuery}
                onChange={(e) => searchJobs(e.target.value)}
                placeholder="Search by job code or title…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {jobResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                  {jobResults.map((p) => (
                    <button key={p.id || p._id} onClick={() => pickJob(p)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2">
                      <span className="font-medium">{p.job_code || p.job_number}</span>
                      <span className="text-gray-500 truncate flex-1">{p.title}</span>
                      {p.archived && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                          delivered
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <input
                value={form.reported_by_client || ''}
                onChange={(e) => setForm((f) => ({ ...f, reported_by_client: e.target.value }))}
                placeholder="Reported by (client contact)"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <input
                type="date"
                value={form.target_date || ''}
                onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </section>

          {/* Problem */}
          <section className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h2 className="text-sm font-semibold text-gray-800">What happened</h2>
            <textarea
              rows={3}
              value={form.problem_description || ''}
              onChange={(e) => setForm((f) => ({ ...f, problem_description: e.target.value }))}
              placeholder="Problem description — what the client reported"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <textarea
              rows={2}
              value={form.immediate_action || ''}
              onChange={(e) => setForm((f) => ({ ...f, immediate_action: e.target.value }))}
              placeholder="Immediate / containment action taken"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </section>

          {/* 5 Why */}
          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-800">Root cause — 5 Why</h2>
            <p className="text-xs text-gray-500 mb-3">
              Each answer asks "why?" of the one above it. The fifth is usually
              close to the real root cause.
            </p>
            <div className="space-y-2">
              {(form.whys || []).map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-16 shrink-0 text-xs font-semibold text-gray-500">Why {i + 1}</span>
                  <input
                    value={w}
                    onChange={(e) => setForm((f) => ({
                      ...f, whys: f.whys.map((x, j) => (j === i ? e.target.value : x)),
                    }))}
                    placeholder={i === 0 ? 'Why did the problem happen?' : 'Why did that happen?'}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              ))}
            </div>
            <textarea
              rows={2}
              value={form.root_cause || ''}
              onChange={(e) => setForm((f) => ({ ...f, root_cause: e.target.value }))}
              placeholder="Root cause (conclusion)"
              className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </section>

          {/* People */}
          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-800">People involved</h2>
            <p className="text-xs text-gray-500 mb-3">
              Only people who completed work on this job are listed. Each selection
              costs them <strong>{form.penalty_multiplier}&times;</strong> the points
              they earned on that task, in the month this CAPA is raised.
            </p>

            {!form.project_id ? (
              <p className="text-sm text-gray-400 py-4 text-center">Choose a job first.</p>
            ) : loadingPeople ? (
              <p className="text-sm text-gray-400 py-4 text-center animate-pulse">Loading…</p>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">
                Nobody has completed scored work on this job yet.
              </p>
            ) : (
              <div className="space-y-2">
                {candidates.map((c) => (
                  <div key={c.user_id} className="border border-gray-200 rounded-md p-2">
                    <div className="text-sm font-medium text-gray-800">
                      {c.user_name}
                      <span className="ml-2 text-xs text-gray-500">{c.role}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {c.tasks.map((t) => (
                        <label key={t.task_id}
                          className={`flex items-center gap-2 text-xs border rounded px-2 py-1 cursor-pointer ${
                            isPicked(c.user_id, t.task_id)
                              ? 'bg-red-50 border-red-300 text-red-800'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}>
                          <input
                            type="checkbox"
                            checked={isPicked(c.user_id, t.task_id)}
                            onChange={() => togglePerson(c, t)}
                            className="w-3.5 h-3.5 accent-red-600"
                          />
                          {t.task_name}
                          <span className="font-semibold">{t.earned_points} pts</span>
                          {t.round > 1 && <span className="text-gray-400">R{t.round}</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {form.people.length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-3 space-y-3">
                {form.people.map((p, i) => (
                  <div key={`${p.user_id}-${p.task_id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-800">
                        {p.user_name} <span className="text-gray-500">— {p.task_name}</span>
                      </span>
                      <span className="text-red-700 font-semibold">
                        {p.base_points} &times; {form.penalty_multiplier} = &minus;
                        {(Number(p.base_points) * Number(form.penalty_multiplier)).toFixed(2)} pts
                      </span>
                    </div>
                    <textarea
                      rows={2}
                      value={p.reason}
                      onChange={(e) => setReason(i, e.target.value)}
                      placeholder="Why was this person involved? (required before the CAPA can be opened)"
                      className={`w-full mt-1 px-3 py-2 border rounded-md text-sm ${
                        p.reason.trim().length < 5 ? 'border-amber-300 bg-amber-50/40' : 'border-gray-300'
                      }`}
                    />
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-sm text-gray-600">Total penalty</span>
                  <span className="text-lg font-bold text-red-700">
                    &minus;{totalPenalty.toFixed(2)} pts
                  </span>
                </div>
              </div>
            )}
          </section>

          {/* Actions */}
          <section className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h2 className="text-sm font-semibold text-gray-800">Actions</h2>
            <textarea
              rows={2}
              value={form.corrective_action || ''}
              onChange={(e) => setForm((f) => ({ ...f, corrective_action: e.target.value }))}
              placeholder="Corrective action — fixes this occurrence"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <textarea
              rows={2}
              value={form.preventive_action || ''}
              onChange={(e) => setForm((f) => ({ ...f, preventive_action: e.target.value }))}
              placeholder="Preventive action — stops it happening again"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <input
              value={form.responsible_user_name || ''}
              onChange={(e) => setForm((f) => ({ ...f, responsible_user_name: e.target.value }))}
              placeholder="Responsible for the actions"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </section>

          <div className="flex flex-wrap justify-end gap-2 pb-8">
            <button onClick={() => setEditing(null)}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            {form.status !== 'open' && form.status !== 'closed' && (
              <button onClick={() => save('draft')} disabled={saving}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save as draft'}
              </button>
            )}
            {editing !== 'new' && form.status !== 'cancelled' && (
              <button onClick={() => save('cancelled')} disabled={saving}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Withdraws the CAPA and removes its penalty">
                Cancel CAPA
              </button>
            )}
            {form.status === 'open' && (
              <button onClick={() => save('closed')} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                title="Actions verified — the penalty stays">
                {saving ? 'Saving…' : 'Close CAPA'}
              </button>
            )}
            {form.status !== 'closed' && (
              <button onClick={() => save('open')} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
                {saving ? 'Saving…'
                  : form.status === 'open' ? 'Save changes' : 'Open CAPA — applies the penalty'}
              </button>
            )}
            {form.status === 'closed' && (
              <button onClick={() => save('closed')} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-blue-600" /> CAPA
        </h1>
        {canManage && (
          <button onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New CAPA
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Corrective and preventive actions raised against client-reported errors,
        with 5 Why root cause analysis.
      </p>

      {error && (
        <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}
          placeholder="Filter by job code…"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <p className="py-12 text-center text-gray-400 animate-pulse">Loading…</p>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-gray-400">No CAPAs yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">CAPA</th>
                <th className="text-left px-3 py-2 font-medium">Job</th>
                <th className="text-left px-3 py-2 font-medium">Problem</th>
                <th className="text-right px-3 py-2 font-medium">People</th>
                <th className="text-right px-3 py-2 font-medium">Penalty</th>
                <th className="text-left px-3 py-2 font-medium">Month</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-blue-50/40">
                  <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{c.capa_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="font-medium text-gray-800">{c.job_code}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[14rem]">{c.project_title}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    <div className="line-clamp-2 max-w-md">{c.problem_description}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700">{c.people_count}</td>
                  <td className="px-3 py-2 text-right font-semibold text-red-700">
                    {c.total_penalty_points ? `−${c.total_penalty_points}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{c.penalty_month}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${
                      STATUS_STYLE[c.status] || STATUS_STYLE.draft}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {/* Export is manager-only on the server, so it is hidden
                        from operators rather than showing them a 403. */}
                    {canManage && (
                      <button onClick={() => exportPdf(c.id)} title="Export as PDF"
                        className="text-gray-500 hover:text-blue-700 mr-2"><FileDown className="w-4 h-4" /></button>
                    )}
                    {canManage && (
                      <button onClick={() => openExisting(c.id)} title="Open"
                        className="text-gray-500 hover:text-blue-700 mr-2"><ChevronRight className="w-4 h-4" /></button>
                    )}
                    {user?.role === 'admin' && (
                      <button onClick={() => remove(c.id)} title="Delete"
                        className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages} · {pagination.total} total
          </span>
          <div className="flex gap-2">
            <button onClick={() => load(pagination.page - 1)} disabled={pagination.page <= 1}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm disabled:opacity-40">← Prev</button>
            <button onClick={() => load(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm disabled:opacity-40">Next →</button>
          </div>
        </div>
      )}

      {!canManage && (
        <p className="mt-4 text-xs text-gray-500 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          You are seeing only CAPAs that name you.
        </p>
      )}
    </div>
  );
}
